import React, { useState, useEffect, useRef } from "react";
import useSWR from "swr";
import { BASE_URL, getFetcher } from "../../../services/api";
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@nextui-org/react";
import { ArrowDownTrayIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { KUNCI_PENGUMUMAN_DITUTUP, sudahDitutupSesiIni } from "../../../services/pengumuman";
import { jadikanTautan } from "../../../services/tautan";

/**
 * Pengumuman untuk pengguna, disusun seperti papan pengumuman SIMPEL.
 *
 * Bedanya dengan versi lama: dulu hanya SATU pengumuman (data[0]) yang tampil,
 * sisanya baru muncul setelah yang pertama ditutup - pengumuman yang menumpuk
 * praktis tidak terbaca. Sekarang semuanya ditampilkan sekaligus dalam daftar
 * bergulir, masing-masing dengan tanggal, pembatas, dan tombol unduh lampiran
 * bila ada berkas yang diunggah saat pembuatan.
 *
 * Tombol OK hanya MENUTUP, tidak menandai apa pun sebagai sudah dibaca, jadi
 * pengumuman muncul lagi pada login berikutnya. Penandanya disimpan di
 * sessionStorage - bukan di basis data - supaya pengumuman tidak muncul ulang
 * setiap kali berpindah halaman dalam sesi yang sama, dan penanda itu dibuang
 * saat keluar (lihat services/pengumuman.js).
 */
/*
 * Penjaga agar kotak pengumuman hanya dirender SATU kali.
 *
 * Komponen ini dipasang di beberapa tempat sekaligus: AuthProvider (yang di
 * daftar rute bahkan bersarang di dalam dirinya sendiri) dan RouteGuest.
 * Akibatnya dua modal identik bertumpuk di halaman admin. Daripada mengobrak
 * daftar rute yang dipakai seluruh aplikasi, pemasangan pertama yang menang;
 * pemasangan berikutnya tidak menggambar apa pun sampai yang pertama dilepas.
 */
let pemegangSlot = null;

const AnnouncementBanner = ({ userId }) => {
  const url = userId ? `/api/announcements/unseen/${userId}` : "/api/announcements/search?isActive=true&isPublic=true&type=Banner";
  const { data, error } = useSWR(url, getFetcher);
  const [isVisible, setIsVisible] = useState(false);

  const daftar = data?.data ?? [];

  // Penanda instans; dibandingkan dengan pemegang slot di atas.
  const penanda = useRef(Symbol("pengumuman"));
  const [bolehTampil, setBolehTampil] = useState(false);

  useEffect(() => {
    if (pemegangSlot === null) {
      pemegangSlot = penanda.current;
      setBolehTampil(true);
    }

    return () => {
      if (pemegangSlot === penanda.current) {
        pemegangSlot = null;
      }
    };
  }, []);

  useEffect(() => {
    if (daftar.length > 0 && !error && !sudahDitutupSesiIni()) {
      setIsVisible(true);
    }
  }, [daftar.length, error]);

  function tutup() {
    // Hanya menutup. Tidak ada yang dicatat di basis data, sehingga pengumuman
    // yang sama disodorkan lagi pada login berikutnya.
    try {
      sessionStorage.setItem(KUNCI_PENGUMUMAN_DITUTUP, "1");
    } catch (galat) {
      // Mode privat atau penyimpanan diblokir: tidak apa-apa, paling-paling
      // pengumuman muncul lagi saat berpindah halaman.
    }
    setIsVisible(false);
  }

  if (!bolehTampil || !daftar.length || !isVisible || error) return null;

  return (
    <Modal
      size="4xl"
      isOpen={isVisible}
      onOpenChange={tutup}
      scrollBehavior="inside"
      className="rounded-xl shadow-lg bg-white"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 border-b border-line">
          <span className="flex items-center gap-2 text-lg text-ink-900">
            <InformationCircleIcon className="size-5 text-primary" />
            Informasi
          </span>
          <span className="text-xs font-normal text-ink-500">
            {daftar.length} pengumuman untuk Anda. Tekan <b>OK</b> untuk menutup - pengumuman ini akan ditampilkan lagi saat Anda masuk berikutnya.
          </span>
        </ModalHeader>

        {/* Daftar bergulir: tinggi dibatasi supaya tombol OK selalu terlihat
            tanpa harus menggulir sampai habis. */}
        <ModalBody className="max-h-[65vh] overflow-y-auto py-0">
          {daftar.map((pengumuman) => (
            /* Tiap pengumuman berdiri sebagai kartu: kepalanya berlatar
               surface-2 dan berpembatas, isinya berlatar surface. Sebelumnya
               judul dan isi sama-sama polos sehingga pada pengumuman panjang
               sulit terlihat di mana satu pengumuman berakhir dan yang
               berikutnya mulai. Warna diambil dari token design system, bukan
               nilai lepas. */
            <div
              key={pengumuman.id}
              className="shrink-0 rounded-ds-md border border-line overflow-hidden my-4 first:mt-5 last:mb-5"
              /* shrink-0 wajib: badan modal adalah kolom flex, dan tanpa ini tiap
                 kartu menyusut sampai nyaris nol tinggi ketika pengumumannya
                 lebih panjang dari ruang yang tersedia. */
            >
              <div className="flex flex-col gap-1 border-b border-line bg-surface-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <h4 className="text-base font-semibold text-ink-900 min-w-0 break-words">
                  {pengumuman.title}
                </h4>
                <span className="shrink-0 text-xs text-ink-600">
                  {formatTanggal(pengumuman.tanggalBerlaku || pengumuman.createdAt)}
                </span>
              </div>

              <div className="min-w-0 bg-surface px-4 py-4">

                {pengumuman.url && (
                  <img
                    src={`${BASE_URL}/${pengumuman.url}`}
                    alt={pengumuman.title}
                    className="max-h-64 w-auto rounded-md mb-3 cursor-pointer"
                    // Berkas poster bisa saja hilang dari server sementara datanya
                    // masih menyimpan alamatnya. Daripada menampilkan ikon gambar
                    // rusak di tengah pengumuman, gambarnya disembunyikan saja.
                    onError={(event) => { event.currentTarget.style.display = "none"; }}
                    onClick={() => window.open(`${BASE_URL}/${pengumuman.url}`, "_blank", "noopener")}
                  />
                )}

                {pengumuman.description && (
                  <div
                    className="text-sm text-ink-700 leading-6 break-words [&_a]:text-primary [&_a]:underline"
                    // Alamat yang ditulis sebagai teks biasa di CMS diubah jadi
                    // tautan yang bisa diklik; yang sudah berupa tautan dibiarkan.
                    dangerouslySetInnerHTML={{ __html: jadikanTautan(pengumuman.description) }}
                  />
                )}

                {pengumuman.lampiranUrl && (
                  <Button
                    size="sm"
                    color="primary"
                    variant="flat"
                    className="mt-3"
                    startContent={<ArrowDownTrayIcon className="size-4" />}
                    onPress={() => window.open(`${BASE_URL}/${pengumuman.lampiranUrl}`, "_blank", "noopener")}
                  >
                    Unduh Lampiran
                  </Button>
                )}
              </div>
            </div>
          ))}
        </ModalBody>

        <ModalFooter className="border-t border-line">
          <Button size="sm" color="primary" onPress={tutup}>
            OK
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/** Tanggal ringkas ala papan pengumuman: 24-02-2026. */
function formatTanggal(nilai) {
  if (!nilai) return "-";

  const tanggal = new Date(nilai);
  if (isNaN(tanggal)) return "-";

  const duaAngka = (angka) => String(angka).padStart(2, "0");
  return `${duaAngka(tanggal.getDate())}-${duaAngka(tanggal.getMonth() + 1)}-${tanggal.getFullYear()}`;
}

export default AnnouncementBanner;
